import { ApolloClient, NormalizedCacheObject, useMutation } from '@apollo/client';
import { AxiosError, AxiosResponse } from 'axios';
import {
  DeleteAnswerData,
  DeleteAnswerVars,
  UpsertAnswerData,
  UpsertAnswerVars
} from 'dashboard/gql-types';

import { ApiAnswer } from '../models/ApiAnswer';
import ApolloClientService from '../../shared/services/ApolloClientService';
import Config from '../../shared/config';
import DashboardGraphQL from '../gql';
import useSWR from 'swr';

let dashboardApolloClient: ApolloClient<NormalizedCacheObject> = null;

export function useAnswers(
  userkey: number,
  lessonkey: number,
  layer: number,
  loadAnswers: boolean = true
) {
  if (!dashboardApolloClient) {
    dashboardApolloClient = ApolloClientService.create(Config.DASHBOARD_GQL);
  }
  const { data: response, error } = useSWR<
    AxiosResponse<ApiAnswer[]>,
    AxiosError<Error>
  >(
    loadAnswers
      ? `${Config.API}/api/portfolio/list/answer/user/${userkey}/module/${lessonkey}/layer/${layer}`
      : null
  );
  if (error) {
    console.error(error);
  }

  const [
    _upsertAnswer,
    { loading: savingAnswer, error: saveAnswerError, data: answer }
  ] = useMutation<UpsertAnswerData, UpsertAnswerVars>(
    DashboardGraphQL.Mutation.UPSERT_ANSWER,
    {
      client: dashboardApolloClient
    }
  );

  const upsertAnswer = (
    componentkey: number,
    answer: string,
    points: number
  ) => {
    return _upsertAnswer({
      variables: {
        userkey,
        modulekey: lessonkey,
        layer,
        componentkey,
        answer,
        points
      }
    });
  };

  const [
    _deleteAnswer,
    {
      loading: deletingAnswer,
      error: deleteAnswerError,
      data: deletedAnswerData
    }
  ] = useMutation<DeleteAnswerData, DeleteAnswerVars>(
    DashboardGraphQL.Mutation.DELETE_ANSWER,
    {
      client: dashboardApolloClient
    }
  );

  const deleteAnswer = (componentkey: number) => {
    return _deleteAnswer({
      variables: {
        userkey,
        modulekey: lessonkey,
        layer,
        componentkey
      }
    });
  };

  return {
    loadingAnswer: !response,
    loadAnswerError: error,
    answers: error?.code === '404' ? null : response?.data,
    upsertAnswer,
    savingAnswer,
    saveAnswerError,
    savedAnswer: answer?.upsertAnswer,
    deleteAnswer,
    deletingAnswer,
    deleteAnswerError,
    deletedAnswer: deletedAnswerData?.deleteAnswer
  };
}
